/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MapConfigService } from '@map/services/map-config.service';
import { MapService } from '@map/services/map.service';
import { MapInteractionsService } from '@map/services/mapInteractions.service';
import { QueryService } from '@map/services/query.service';
import { SelectionFeatures } from '@map/state/selection.model';
import { SelectionState } from '@map/state/selection.state';
import { Store } from '@ngxs/store';
import { DataFormDialogComponent } from '@reports/dataForm/data-form-dialog.component';
import { LoadDataForm } from '@reports/state/data-form.actions';
import { DataFormDialogData, LoadDataFormPayload } from '@reports/state/data-form.model';
import { FeatureReportDefinitionCollection } from '@reports/state/report.model';
import { ReportState } from '@reports/state/report.state';
import { RestService } from '@shared/services/rest.service';

import { Coordinate } from 'ol/coordinate';
import { combineLatest, Observable, Subject, zip } from 'rxjs';
import { concatMap, filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { DigitizePointConfigService } from './digitize-point-config.service';
import { DigitizableLayer, DigitizedFeature } from './state/digitize-point-config.model';

@Injectable({ providedIn: 'root' })
export class DigitizePointService {
    potentiallyDigitizableLayers$: Observable<DigitizableLayer[]>;
    digitizableLayers$: Observable<DigitizableLayer[]>;

    private featureReports$: Observable<FeatureReportDefinitionCollection> = inject(Store).select(ReportState.featureReports);
    private selection$: Observable<SelectionFeatures> = inject(Store).select(SelectionState.selection);

    /**
     * @param rest {RestService}
     * @param dialog {MatDialog}
     * @param mapConfig {MapConfigService}
     * @param digitizeConfig {DigitizePointConfigService}
     * @param mapService {MapService}
     * @param queryService {QueryService}
     * @param mapInteractions {MapInteractionsService}
     * @param store {Store}
     */
    constructor(
        private rest: RestService,
        private dialog: MatDialog,
        private mapConfig: MapConfigService,
        private digitizeConfig: DigitizePointConfigService,
        private mapService: MapService,
        private queryService: QueryService,
        private mapInteractions: MapInteractionsService,
        private store: Store
    ) {
        this.potentiallyDigitizableLayers$ = this.digitizeConfig.layers$.pipe(
            map((layers) => layers.filter((l) => l.create))
        );

        this.digitizableLayers$ = combineLatest([ this.potentiallyDigitizableLayers$, this.selection$ ]).pipe(
            map(([ layers, selection ]) =>
                layers.filter((digitizeLayer) => {
                    const requiredSelectionLayer = digitizeLayer.requireSelection;
                    if (requiredSelectionLayer) {
                        const layerSelection = selection[requiredSelectionLayer];
                        return layerSelection && layerSelection.length === 1;
                    }
                    return true;
                })
            )
        );
    }

    public canMovePoint$(layerName): Observable<boolean> {
        return this.digitizeConfig.getLayer(layerName).pipe(map((layer) => layer && layer.move));
    }

    public canDeletePoint$(layerName): Observable<boolean> {
        return this.digitizeConfig.getLayer(layerName).pipe(map((layer) => layer && layer.delete));
    }

    public canCreatePoint$(layerName) {
        return this.digitizableLayers$.pipe(
            map((layers) => {
                const layerConfig = layers.find((l) => l.layer === layerName);
                return layerConfig && layerConfig.create;
            })
        );
    }

    public addPoint(layerName: string, finishedSignal$?: Subject<void>): void {
        // ensure layer is visible
        this.mapConfig.showLayers(layerName, true);
        const processFn = this.processSavePoint.bind(this, finishedSignal$);
        setTimeout(() => {
            this.mapInteractions.enableDigitizePointInteraction(processFn, layerName);
        }, 0);
    }

    public movePoint(layerName: string, key: any): void {
        const processFn = this.processMovePoint.bind(this);
        this.mapInteractions.enableDigitizePointInteraction(processFn, layerName, key);
    }

    public deletePoint(layerName: string, key: any): void {
        const data = {
            key,
            layer: layerName
        };

        const layers = this.mapConfig.getVisibleLayerList(this.mapService.currentScale).map((layer) => layer.name);

        this.rest
            .apiPost(`map/digitize/delete/${ this.mapConfig.mapName }`, data)
            .pipe(
                tap(() => {
                    this.mapService.refreshImageWMSLayers('layers', layers);
                })
            )
            .subscribe(() => {
                this.queryService.clearSelection();
            });
    }

    public processSavePoint(finishedSignal$: Subject<void>, layerName: string, _: any, coordinates: Coordinate) {
        const data: any = {
            coordinates,
            layer: layerName
        };
        const layerConfig$ = this.digitizeConfig.getLayer(layerName);
        const transformPoint$ = this.rest
            .apiPost<Coordinate>(`map/digitize/transform/${ this.mapConfig.mapName }`, data)
            .pipe(take(1));

        const initialSelection$ = layerConfig$.pipe(
            filter((dl) => !!dl),
            switchMap((layerConfig) => this.queryService.getSingleSelection$(layerConfig.requireSelection))
        );

        const dataFormName$: Observable<string> = zip(this.featureReports$, layerConfig$).pipe(
            map(([ reportCollection, layerConfig ]) => {
                const layerReports = reportCollection[layerName].filter((r) => r.subType === 'detail' && r.dataFormName);
                return layerConfig.dataForm || (layerReports.length === 1 ? layerReports[0].dataFormName : null);
            }),
            shareReplay()
        );

        zip(dataFormName$, initialSelection$)
            .pipe(
                map(
                    ([ dataFormName, initialSelection ]): LoadDataFormPayload => ({
                        name: dataFormName,
                        initialSelection
                    })
                ),
                switchMap((payload) => this.store.dispatch(new LoadDataForm(payload))),
                concatMap(() => zip(dataFormName$, layerConfig$, transformPoint$)),
                concatMap(([ dataFormName, config, coords ]) =>
                    this.dialog
                        .open<DataFormDialogComponent, DataFormDialogData>(DataFormDialogComponent, {
                            data: {
                                name: dataFormName,
                                fields: [
                                    {
                                        name: config.latField,
                                        value: coords[1]
                                    },
                                    {
                                        name: config.lonField,
                                        value: coords[0]
                                    }
                                ]
                            }
                        })
                        .afterClosed()
                ),
                withLatestFrom(layerConfig$),
                concatMap(([ keyValue, config ]) => this.queryService.selectSingleFeature(layerName, config.keyField, keyValue))
            )
            .subscribe(() => finishedSignal$?.next());
    }

    public processMovePoint(layerName, key, coordinates) {
        const data: any = {
            coordinates,
            key,
            layer: layerName
        };

        const postResult$ = this.rest
            .apiPost(`map/digitize/move/${ this.mapConfig.mapName }`, data)
            .pipe(take(1), shareReplay());

        postResult$
            .pipe(
                map(() => this.mapConfig.getVisibleLayerList(this.mapService.currentScale)),
                map((layerList) => layerList.map((layer) => layer.name)),
                tap((layers) => this.mapService.refreshImageWMSLayers('layers', layers))
            )
            .subscribe(() => this.mapInteractions.enablePanInteraction());

        postResult$.subscribe((featureInfo: DigitizedFeature) => {
            // select moved feature
            this.queryService.selectSingleFeature(layerName, featureInfo.keyfield, featureInfo.key);
        });
    }

    public getLayerKeyField(layerName): Observable<string> {
        if (layerName) {
            return this.digitizeConfig.getLayer(layerName).pipe(
                filter((layer) => !!layer),
                map((layer: DigitizableLayer) => layer.keyField)
            );
        }
    }
}
